import { useState } from 'react';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { useAuth } from '../../../../shared/useAuth.js';
import {
  usePackages,
  useCreatePackage,
  useUpdatePackage,
  useDeletePackage
} from '../../hooks/usePackages.js';
import { PackageCard } from './PackageCard.jsx';
import { PackageForm } from './PackageForm.jsx';
import { ConfirmModal } from '../ConfirmModal.jsx';

export function PackagesList() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canEdit = hasPermission('pricing.edit');
  const { data: packages = [], isLoading, error } = usePackages();
  const createPkg = useCreatePackage();
  const updatePkg = useUpdatePackage();
  const deletePkg = useDeletePackage();

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(pkg) {
    setEditing(pkg);
    setFormOpen(true);
  }
  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  async function handleSubmit(body) {
    if (editing) {
      await updatePkg.mutateAsync(
        { id: editing._id || editing.id, body },
        { onSuccess: () => closeForm() }
      );
    } else {
      await createPkg.mutateAsync(body, { onSuccess: () => closeForm() });
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[15px] font-bold">الباقات</div>
          <div className="text-xs text-ink-3 mt-0.5">باقات الدخول الجاهزة</div>
        </div>
        {canEdit ? (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" size={14} /> باقة جديدة
          </button>
        ) : null}
      </div>

      {error ? (
        <Card>
          <div className="text-red text-center py-4">تعذّر تحميل الباقات</div>
        </Card>
      ) : isLoading ? (
        <Card>
          <div className="text-ink-3 text-center py-8">جاري التحميل...</div>
        </Card>
      ) : packages.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-10 flex flex-col items-center gap-3">
            <Icon name="pricing" size={42} color="#D6CCB3" />
            <div className="text-[14px] font-semibold text-ink-2">
              لا توجد باقات بعد — أنشئ باقتك الأولى
            </div>
            {canEdit ? (
              <button type="button" className="btn btn-primary" onClick={openCreate}>
                <Icon name="plus" size={14} /> باقة جديدة
              </button>
            ) : null}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {packages.map((p) => (
            <PackageCard
              key={p._id || p.id}
              pkg={p}
              canEdit={canEdit}
              onEdit={openEdit}
              onDelete={(pkg) => setConfirmDelete(pkg)}
            />
          ))}
        </div>
      )}

      <PackageForm
        open={formOpen}
        pkg={editing}
        onClose={closeForm}
        onSubmit={handleSubmit}
        submitting={createPkg.isPending || updatePkg.isPending}
      />

      <ConfirmModal
        open={!!confirmDelete}
        title="حذف الباقة"
        message={`هل أنت متأكد من حذف الباقة "${confirmDelete?.name || ''}"؟`}
        confirmLabel="حذف"
        variant="danger"
        loading={deletePkg.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deletePkg.mutateAsync(
            confirmDelete._id || confirmDelete.id,
            { onSuccess: () => setConfirmDelete(null) }
          );
        }}
      />
    </div>
  );
}
